/**
 * Reusable player card for match flow nodes.
 *
 * Replaces duplicated player row patterns across auto-balance,
 * captain-draft, ready-up, and other node views.
 */

import type { FlowPlayer } from "@rivaled/shared";
import { getMergedThresholds, getRankFromElo, type QueueRankConfig } from "@rivaled/shared";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Copy, Crown, X } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { rankColors, RankIcon } from "../../components/ui/RankIcon";
import { cn } from "../../lib/utils";
import type { PartyColor } from "../lib/party-colors";
import { PlayerAvatar } from "./PlayerAvatar";

type PlayerCardProps = {
  player: FlowPlayer;
  variant?: "compact" | "default" | "pool";
  status?: "ready" | "waiting" | "idle";
  isMe?: boolean;
  partyColor?: PartyColor | null;
  rankConfig?: QueueRankConfig | null;
  badges?: ReactNode;
  rightSlot?: ReactNode;
  gameIdentity?: string | null;
  animationDelay?: number;
  animateFrom?: "left" | "right" | "bottom";
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
};

const variantStyles = {
  compact: {
    avatar: "md" as const,
    px: "px-2.5 py-2",
    avatarSize: "md" as const,
    rankSize: "md" as const,
    showLevels: true as const,
  },
  default: {
    avatar: "lg" as const,
    px: "px-3 py-2.5",
    avatarSize: "lg" as const,
    rankSize: "md" as const,
    showLevels: true as const,
  },
  pool: {
    avatar: "lg" as const,
    px: "px-3 py-2.5",
    avatarSize: "lg" as const,
    rankSize: "md" as const,
    showLevels: true as const,
  },
} as const;

const animationOrigin = {
  left: { x: -12 },
  right: { x: 12 },
  bottom: { y: 12 },
} as const;

export function PlayerCard({
  player,
  variant = "default",
  status,
  isMe = false,
  partyColor,
  rankConfig,
  badges,
  rightSlot,
  gameIdentity,
  animationDelay = 0,
  animateFrom = "left",
  onClick,
  disabled,
  className,
}: PlayerCardProps) {
  const styles = variantStyles[variant];
  const isReady = status === "ready";
  const isWaiting = status === "waiting";

  // Rank resolution
  const thresholds = rankConfig ? getMergedThresholds(rankConfig) : null;
  const rank = thresholds ? getRankFromElo(player.displayElo, thresholds) : null;

  // Avatar ring color — ready state overrides
  const avatarRing = isReady
    ? "ring-brand-500/50 bg-brand-500/15 text-brand-400"
    : isMe
      ? "ring-brand-500/50 bg-brand-500/15 text-brand-400"
      : partyColor
        ? `${partyColor.ring} bg-surface-700 text-surface-400`
        : "ring-surface-600/40 bg-surface-700 text-surface-400";

  // Card background — ready state gets brand tint
  const cardBg = isReady
    ? "bg-brand-500/[0.07] ring-1 ring-brand-500/25"
    : isMe
      ? "bg-brand-500/[0.08] ring-1 ring-brand-500/20"
      : partyColor
        ? `${partyColor.bg} bg-surface-875`
        : "bg-surface-875";

  // Name color
  const nameColor = isMe ? "text-brand-300" : "text-text-primary";

  // Pool variant specifics
  const isPoolInteractive = variant === "pool" && onClick && !disabled;

  const Tag = onClick ? motion.button : motion.div;

  return (
    <Tag
      initial={{ opacity: 0, ...animationOrigin[animateFrom] }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      exit={
        variant === "pool" ? { opacity: 0, scale: 0.9, transition: { duration: 0.2 } } : undefined
      }
      transition={{
        duration: 0.3,
        delay: animationDelay,
        ...(variant === "pool" ? { type: "spring", stiffness: 300, damping: 25 } : {}),
      }}
      whileHover={isPoolInteractive ? { scale: 1.02 } : undefined}
      whileTap={isPoolInteractive ? { scale: 0.98 } : undefined}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "relative flex items-center gap-2.5 rounded-md transition-colors",
        styles.px,
        cardBg,
        variant === "pool" && onClick
          ? cn(
              "rounded-lg text-left transition-all duration-200",
              "focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 focus:ring-offset-surface-900",
              !disabled
                ? "bg-surface-800/60 border border-surface-700/50 hover:border-brand-500/50 hover:bg-surface-800/80 hover:shadow-[0_0_12px_rgba(38,217,181,0.1)] cursor-pointer"
                : "bg-surface-800/40 border border-surface-700/30 cursor-not-allowed opacity-60",
            )
          : "",
        className,
      )}
    >
      {/* Ready flash overlay */}
      <AnimatePresence>
        {isReady && (
          <motion.div
            initial={{ opacity: 0.6 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="absolute inset-0 rounded-md bg-brand-500/20 pointer-events-none"
          />
        )}
      </AnimatePresence>

      {/* Avatar */}
      <div className="relative shrink-0">
        <PlayerAvatar
          avatarUrl={player.avatarUrl}
          username={player.username}
          size={styles.avatarSize}
          ringColor={avatarRing}
          className={cn(player.isConnected === false && "opacity-40 grayscale")}
        />
        {/* Ready indicator dot */}
        <AnimatePresence>
          {isReady && variant !== "compact" && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              transition={{ type: "spring", stiffness: 500, damping: 25 }}
              className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-brand-500 border-2 border-surface-900 flex items-center justify-center"
            >
              <Check className="w-1.5 h-1.5 text-surface-900" strokeWidth={4} />
            </motion.div>
          )}
        </AnimatePresence>
        {player.isConnected === false && !isReady && (
          <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-surface-900 flex items-center justify-center">
            <div className="w-2.5 h-2.5 rounded-full bg-error-500/80 flex items-center justify-center">
              <X className="w-1.5 h-1.5 text-white" strokeWidth={4} />
            </div>
          </div>
        )}
      </div>

      {/* Name + identity column */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            title={player.username}
            className={cn(
              "truncate text-sm font-medium",
              nameColor,
              player.isConnected === false && "opacity-50",
            )}
          >
            {player.username ?? "Unknown"}
          </span>
          {player.isHost && <Crown className="h-3 w-3 text-amber-400 shrink-0" />}
        </div>
        {gameIdentity && <GameIdentityRow identity={gameIdentity} />}
      </div>

      {/* Badges slot (CPT, PARTY, etc.) */}
      {badges}

      {/* Status indicator (ready/waiting) */}
      {(isReady || isWaiting) && (
        <div className="shrink-0">
          <AnimatePresence mode="wait">
            {isReady ? (
              <motion.div
                key="check"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 25 }}
              >
                <Check
                  className={cn(
                    "text-brand-400",
                    variant === "compact" ? "h-3.5 w-3.5" : "h-4 w-4",
                  )}
                  strokeWidth={2.5}
                />
              </motion.div>
            ) : (
              <motion.div
                key="waiting"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={cn(
                  "rounded-full border-2 border-surface-600/60 border-t-surface-400/60 animate-spin",
                  variant === "compact" ? "w-3.5 h-3.5" : "w-4 h-4",
                )}
                style={{ animationDuration: "1.5s" }}
              />
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Right slot: rank icon + ELO or custom content */}
      {rightSlot ??
        (rank && thresholds ? (
          <div className="shrink-0 flex items-center gap-1.5">
            <RankIcon rank={rank} size={styles.rankSize} showLevels={false} />
            <span className={cn("text-xs tabular-nums font-semibold", rankColors[rank].text)}>
              {player.displayElo}
            </span>
          </div>
        ) : (
          <span
            className={cn(
              "shrink-0 text-xs tabular-nums font-semibold px-1.5 py-0.5 rounded",
              isMe ? "text-brand-400/80 bg-brand-500/10" : "text-surface-400 bg-surface-800/60",
            )}
          >
            {player.displayElo ?? "\u2014"}
          </span>
        ))}
    </Tag>
  );
}

function GameIdentityRow({ identity }: { identity: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(identity).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex items-center gap-1 group/id cursor-pointer max-w-fit"
    >
      <span className="text-xs text-surface-500 truncate">{identity}</span>
      <Copy
        className={cn(
          "h-2.5 w-2.5 shrink-0 transition-colors",
          copied ? "text-brand-400" : "text-surface-600 opacity-0 group-hover/id:opacity-100",
        )}
      />
    </button>
  );
}
